import '@xterm/xterm/css/xterm.css'
import { Terminal } from '@xterm/xterm'
import { FitAddon } from '@xterm/addon-fit'
import { WebLinksAddon } from '@xterm/addon-web-links'

class ServiceTerminal {
	constructor(container, urls) {
		this.container = container
		this.urls = urls
		this.sessionId = null
		this.offset = 0
		this.pollInterval = null
		this.terminal = null
		this.fitAddon = null
		this.killed = false
		this.polling = false
		this.onEnd = null
	}

	init() {
		if (this.terminal) return

		this.terminal = new Terminal({
			cursorBlink: true,
			fontSize: 14,
			theme: { background: '#1e1e1e' },
			scrollback: 5000,
			convertEol: true,
		})

		this.fitAddon = new FitAddon()
		this.terminal.loadAddon(this.fitAddon)
		this.terminal.loadAddon(new WebLinksAddon())

		this.terminal.open(this.container)
		this.fitAddon.fit()

		this.terminal.onData((data) => {
			this.sendInput(data)
		})

		this.terminal.onResize(({ cols, rows }) => {
			if (this.sessionId) {
				this.sendResize(cols, rows)
			}
		})
	}

	start(command) {
		this.init()

		this.killed = false
		this.sessionId = null
		this.offset = 0

		if (command) {
			this.terminal.write('\x1b[33mRunning: ' + command + '\x1b[0m\r\n')
		} else {
			this.terminal.write('\x1b[33mStarting terminal session...\x1b[0m\r\n')
		}

		const cols = this.terminal.cols
		const rows = this.terminal.rows

		let url = this.urls.startUrl + (this.urls.startUrl.includes('?') ? '&' : '?') + 'cols=' + cols + '&rows=' + rows
		if (command) {
			url += '&command=' + encodeURIComponent(command)
		}

		fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
			.then(r => r.json())
			.then(data => {
				if (data.error) {
					this.terminal.write('\x1b[31mError: ' + data.error + '\x1b[0m\r\n')
					if (this.onEnd) this.onEnd()
					return
				}
				this.sessionId = data.sessionId
				this.pollInterval = setInterval(() => this.pollOutput(), 250)
			})
			.catch(err => {
				this.terminal.write('\x1b[31mFailed to start session\x1b[0m\r\n')
				if (this.onEnd) this.onEnd()
			})
	}

	pollOutput() {
		if (this.killed || !this.sessionId || this.polling) return
		this.polling = true

		const separator = this.urls.outputUrl.includes('?') ? '&' : '?'
		const url = this.urls.outputUrl + separator + 'sessionId=' + this.sessionId + '&offset=' + this.offset

		fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
			.then(r => r.json())
			.then(data => {
				if (data.error) return

				if (data.data) {
					const binaryString = atob(data.data)
					const bytes = new Uint8Array(binaryString.length)
					for (let i = 0; i < binaryString.length; i++) {
						bytes[i] = binaryString.charCodeAt(i)
					}
					this.terminal.write(bytes)
				}

				this.offset = data.offset

				if (!data.alive && !data.data) {
					this.terminal.write('\r\n\x1b[33mSession ended.\x1b[0m\r\n')
					if (this.pollInterval) {
						clearInterval(this.pollInterval)
						this.pollInterval = null
					}
					if (this.onEnd) {
						this.onEnd()
					}
				}
			})
			.catch(() => {})
			.finally(() => { this.polling = false })
	}

	sendInput(data) {
		if (this.killed || !this.sessionId) return

		const utf8Bytes = new TextEncoder().encode(data)
		let binaryString = ''
		for (let i = 0; i < utf8Bytes.length; i++) {
			binaryString += String.fromCharCode(utf8Bytes[i])
		}
		const encoded = btoa(binaryString)
		const separator = this.urls.inputUrl.includes('?') ? '&' : '?'
		const url = this.urls.inputUrl + separator + 'sessionId=' + this.sessionId + '&data=' + encodeURIComponent(encoded)

		fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
			.catch(() => {})
	}

	sendResize(cols, rows) {
		if (this.killed || !this.sessionId) return

		const separator = this.urls.resizeUrl.includes('?') ? '&' : '?'
		const url = this.urls.resizeUrl + separator + 'sessionId=' + this.sessionId + '&cols=' + cols + '&rows=' + rows

		fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
			.catch(() => {})
	}

	clear() {
		if (this.terminal) {
			this.terminal.clear()
		}
	}

	kill() {
		if (this.killed) return
		this.killed = true

		if (this.pollInterval) {
			clearInterval(this.pollInterval)
			this.pollInterval = null
		}

		if (this.sessionId) {
			const separator = this.urls.killUrl.includes('?') ? '&' : '?'
			const url = this.urls.killUrl + separator + 'sessionId=' + this.sessionId

			fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
				.catch(() => {})
			this.sessionId = null
		}
	}

	dispose() {
		this.kill()
		if (this.terminal) {
			this.terminal.dispose()
			this.terminal = null
		}
	}
}

window.ServiceTerminal = ServiceTerminal
